import { useEffect, useState } from 'react';
import { Outlet, Navigate } from 'react-router-dom';
import { useAuth } from '@/lib/AuthContext';


const DefaultFallback = () => (
  <div className="fixed inset-0 flex items-center justify-center">
    <div className="w-8 h-8 border-4 border-slate-200 border-t-slate-800 rounded-full animate-spin"></div>
  </div>
);

/**
 * @param {boolean} [skipOrgRedirect] - IC-005: set on the /welcome route only. Every other
 *   protected route redirects a signed-in, non-provider user with no organisation to /welcome
 *   (the real "no organisation" signal is user.organisations, from GET /api/v1/me — see
 *   AuthContext) instead of rendering — nobody should ever land on a 403 wall or a blank app.
 *   /welcome must not redirect to itself, hence the flag.
 */
export default function ProtectedRoute({ fallback = <DefaultFallback />, unauthenticatedElement, skipOrgRedirect = false }) {
  const { isAuthenticated, isLoadingAuth, authChecked, checkUserAuth, user } = useAuth();
  const [isAuthorized, setIsAuthorized] = useState(false);

  useEffect(() => {
    if (!authChecked && !isLoadingAuth) {
      checkUserAuth();
    }
  }, [authChecked, isLoadingAuth, checkUserAuth]);

  useEffect(() => {
    if (isAuthenticated && user) {
      // Server-side membership decides access; any authenticated user with a session is authorised here.
      setIsAuthorized(true);
    } else {
      setIsAuthorized(false);
    }
  }, [isAuthenticated, user]);

  if (isLoadingAuth || !authChecked) {
    return fallback;
  }

  if (!isAuthenticated) {
    return unauthenticatedElement;
  }

  if (!isAuthorized) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-background p-4">
        <div className="max-w-md w-full bg-card border border-border rounded-2xl p-6 text-center">
          <h1 className="text-xl font-bold text-foreground mb-2">Access Denied</h1>
          <p className="text-sm text-muted-foreground">
            This application is restricted to authorized users only.
          </p>
          <p className="text-xs text-muted-foreground mt-2">
            If you believe you should have access, please contact the administrator.
          </p>
        </div>
      </div>
    );
  }

  if (!skipOrgRedirect && Array.isArray(user?.organisations) && user.organisations.length === 0 && !user?.is_provider) {
    return <Navigate to="/welcome" replace />;
  }

  return <Outlet />;
}